/**
 * QUI VOIT L'OPTIMISATION DU TURPE (V2), ET PAR OÙ (DEV-1892).
 * ============================================================================
 *
 * INTENTION : mettre la V2 en production sans la montrer à un client. Décision
 * de Mathieu du 25/09/2026 : « en prod en n'ouvrant que sur notre compte ». La
 * V2 vit donc derrière l'interrupteur de runtime `OPTIMTURPE_V2`, le même
 * mécanisme que la page « Données du compteur » (`docs/DEPLOY.md`) : une
 * variable posée sur le dyno, servie par `GET /api/interrupteurs`, tranchée par
 * le navigateur (`stores/interrupteurs.store.ts`).
 *
 * L'interrupteur S'AJOUTE au gating d'avant, il ne le remplace pas : le module
 * payant `optim-turpe` et le rôle ≥ employé en écriture restent exigés.
 *
 * Interrupteur baissé, ou route des interrupteurs en échec : la session vit
 * STRICTEMENT la V1 d'avant. Pas d'entrée « V2 » au menu, et l'adresse directe
 * de la V2 renvoie vers la V1 (ou vers la page compteur si la V1 elle-même est
 * fermée), avant que le moindre composant V2 ne soit monté.
 */
import type {
    SidebarMenuItem,
    SidebarMenuItemBadge,
} from "@core/layouts/sidebars/sidebar-menu.types"

/** Ce qu'il faut savoir de la session pour ouvrir l'une ou l'autre version. */
export interface AccesOptimTurpe {
    /** Le module payant `optim-turpe` est souscrit. */
    hasModuleOptimTurpe: boolean
    /** Le rôle est au moins « employé en écriture » (table des droits DEV-16282). */
    isEmployeWriteOrHigher: boolean
    /** La session fait partie de la population armée par `OPTIMTURPE_V2`. */
    optimturpeV2Arme: boolean
}

/** Où envoyer une visite de l'adresse de la V2. */
export type DestinationOptimTurpeV2 = "v2" | "v1" | "compteur"

/** Module et rôle : la garde de la V1, inchangée. */
function v1Ouverte(acces: AccesOptimTurpe): boolean {
    return acces.hasModuleOptimTurpe && acces.isEmployeWriteOrHigher
}

/**
 * Où mène l'adresse `/building/:id/optimisation-contractuelle-v2`.
 *
 * Seule une valeur strictement `true` de l'interrupteur ouvre la V2 : tout le
 * reste ferme.
 */
export function destinationOptimTurpeV2(
    acces: AccesOptimTurpe
): DestinationOptimTurpeV2 {
    if (!v1Ouverte(acces)) return "compteur"
    return acces.optimturpeV2Arme === true ? "v2" : "v1"
}

/**
 * Les noms du module quand la V2 est ouverte à la session : D24 du 05/10/2026,
 * maquette mq-menu, variante A (« ancienne version », celle que le dossier
 * retient sans réponse contraire). Un seul nom, « Optimisation du TURPE », au
 * menu comme dans le titre des deux pages.
 *
 * DEV-2311, décision de Mathieu du 07/10/2026 (pistes v0.4, carte 6, variante
 * E) : au menu, les deux entrées portent le même titre « Optimisation du
 * TURPE » et se distinguent par une pastille, « Nouveau » en vert pour la
 * nouvelle version, « Ancien » en gris pour l'ancienne (`PASTILLES_MENU_OPTIMTURPE`).
 * La date d'arrêt de l'ancienne version reste dans sa page (bloc mq-v1).
 */
export const NOMS_OPTIMTURPE_V2_OUVERTE = {
    groupe: "Optimisation contractuelle",
    entreeV2: "Optimisation du TURPE",
    entreeV1: "Optimisation du TURPE",
    titreV2: "Optimisation du TURPE",
    titreV1: "Optimisation du TURPE, ancienne version",
} as const

/**
 * Les pastilles qui distinguent les deux entrées du menu (variante E du
 * 07/10/2026) : teinte de succès (verte) pour la nouvelle version, teinte
 * secondaire (grise) pour l'ancienne.
 */
export const PASTILLES_MENU_OPTIMTURPE = {
    v2: { label: "Nouveau", severity: "success" },
    v1: { label: "Ancien", severity: "secondary" },
} as const satisfies Record<"v1" | "v2", SidebarMenuItemBadge>

/**
 * Les entrées de menu de l'Optimisation du Turpe, dans l'ordre du menu.
 *
 * Décision A13 du 21/09/2026 : quand la V2 est ouverte, les deux versions sont
 * gardées en parallèle et le MENU les distingue. D24 du 05/10/2026 : la
 * nouvelle version vient en premier, sous le nom unique du module. Fermée, le
 * menu est celui d'avant, à l'identique.
 *
 * @param acces - module, rôle et interrupteur de la session
 * @param base - le chemin de la fiche bâtiment (`/building/<id>`)
 * @param libelles - le libellé traduit servi à une session non armée
 */
export function entreesMenuOptimTurpe(
    acces: AccesOptimTurpe,
    base: string,
    libelles: { v1: string }
): SidebarMenuItem[] {
    if (!v1Ouverte(acces)) return []
    const v1: SidebarMenuItem = {
        key: "optimisation-contractuelle",
        label: libelles.v1,
        route: { path: `${base}/optimisation-contractuelle` },
    }
    // Session non armée : le menu d'avant, sans pastille (DEV-1892, la session
    // vit strictement la V1).
    if (destinationOptimTurpeV2(acces) !== "v2") return [v1]
    return [
        {
            key: "optimisation-contractuelle-v2",
            label: NOMS_OPTIMTURPE_V2_OUVERTE.entreeV2,
            badge: { ...PASTILLES_MENU_OPTIMTURPE.v2 },
            route: { path: `${base}/optimisation-contractuelle-v2` },
        },
        {
            ...v1,
            label: NOMS_OPTIMTURPE_V2_OUVERTE.entreeV1,
            badge: { ...PASTILLES_MENU_OPTIMTURPE.v1 },
        },
    ]
}

/**
 * Le libellé du groupe « Optimisation contractuelle » du menu bâtiment : sa
 * casse suit la maquette pour une session armée, le libellé servi sinon.
 */
export function libelleGroupeCourtage(
    optimturpeV2Arme: boolean,
    servi: string
): string {
    return optimturpeV2Arme === true ? NOMS_OPTIMTURPE_V2_OUVERTE.groupe : servi
}

/**
 * Le titre de la page V1 : « Optimisation du TURPE, ancienne version » pour une
 * session armée, le titre servi aujourd'hui sinon.
 */
export function titrePageV1(optimturpeV2Arme: boolean, servi: string): string {
    return optimturpeV2Arme === true
        ? NOMS_OPTIMTURPE_V2_OUVERTE.titreV1
        : servi
}

/**
 * Le bloc « fin de support / arrêt définitif » de la V1 annonce la V2 : tant
 * que la V2 n'est pas ouverte à la session, il reste invisible. Seule une
 * valeur strictement `true` l'affiche (interrupteur non chargé ou en erreur :
 * fermé).
 */
export function afficherCycleDeVieV1(optimturpeV2Arme: boolean): boolean {
    return optimturpeV2Arme === true
}

/** « 2026-12-31 » → « 31/12/2026 » ; une date illisible est rendue telle quelle. */
function dateJJMMAAAA(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim())
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso
}

/**
 * Les lignes du bloc de fin de vie de la V1, texte de la maquette votée mq-v1
 * (D30 du 05/10/2026, après DEV-2311 F5). Ce qui est vrai : l'assistance
 * s'arrête à une date, les études de la V1 restent consultables jusqu'à l'arrêt
 * de la page puis disparaissent avec elle (d'où le conseil de garder ses
 * rapports), et les nouvelles études se font dans la V2. La V2 ne reprend PAS
 * les études de la V1 : la page ne le promet pas.
 *
 * @param finSupport - `FIN_SUPPORT_V1` (AAAA-MM-JJ)
 * @param finDeVie - `FIN_DE_VIE_V1` (AAAA-MM-JJ)
 */
export function lignesCycleDeVieV1(
    finSupport: string,
    finDeVie: string
): {
    titre: string
    support: string
    consultation: string
    nouvelleEtude: string
    lien: string
} {
    return {
        titre: "Ancienne version de l'étude",
        support: `Assistance assurée jusqu'au ${dateJJMMAAAA(finSupport)}.`,
        // DEV-2311 : l'arrêt est conditionnel, comme le dit l'encadré de la
        // nouvelle étude (« au moins jusqu'au »).
        consultation: `Vos études restent consultables ici au moins jusqu'au ${dateJJMMAAAA(finDeVie)}. Téléchargez avant cette date les rapports que vous souhaitez garder.`,
        nouvelleEtude: "Pour une nouvelle étude, utilisez la nouvelle version.",
        lien: "Ouvrir ce compteur dans la nouvelle version",
    }
}

/**
 * La requête du lien de la V1 vers la V2 : le compteur choisi en V1, par le
 * paramètre `idsource` que la V2 lit (`syncSourceFromRoute`). Sans compteur,
 * aucune requête : la V2 prend son premier compteur éligible.
 */
export function routeV2Query(sourceId: string | null | undefined): {
    idsource?: string
} {
    return sourceId ? { idsource: sourceId } : {}
}
